"use client";

import { useState, useEffect, useRef } from "react";

interface GpuData {
  index: number;
  name: string;
  util_pct: number;
  mem_used_mb: number;
  mem_total_mb: number;
  mem_pct: number;
  temp_c: number;
  power_w: number;
  driver_version: string;
}

interface GpuResponse {
  available: boolean;
  gpus: GpuData[];
}

interface CpuData {
  cpu_pct: number;
  cpu_cores: number[];
  ram_pct: number;
  ram_used_gb: number;
  ram_total_gb: number;
}

const HISTORY_LEN = 40;

// Pure SVG sparkline — no external chart library needed
function Sparkline({
  data,
  color,
  height = 36,
}: {
  data: number[];
  color: string;
  height?: number;
}) {
  const W = 120;
  const H = height;
  const pad = 1;

  if (data.length < 2) return <div style={{ width: W, height: H }} />;

  const xs = data.map((_, i) => pad + (i / (data.length - 1)) * (W - pad * 2));
  const ys = data.map((v) => H - pad - (v / 100) * (H - pad * 2));

  const line = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  const area =
    line +
    ` L${xs[xs.length - 1].toFixed(1)},${H - pad} L${xs[0].toFixed(1)},${H - pad} Z`;

  return (
    <svg width={W} height={H} className="overflow-visible">
      <defs>
        <linearGradient id={`grad-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#grad-${color.replace("#", "")})`} />
      <path d={line} stroke={color} strokeWidth="1.5" fill="none" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function Bar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
}

function tempColor(t: number) {
  if (t >= 80) return "#f87171";
  if (t >= 65) return "#fbbf24";
  return "#4ade80";
}

export default function GpuWidget() {
  const [gpuData, setGpuData] = useState<GpuData | null>(null);
  const [cpuData, setCpuData] = useState<CpuData | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);
  const utilHistory = useRef<number[]>([]);
  const memHistory = useRef<number[]>([]);
  const ramHistory = useRef<number[]>([]);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const [resGpu, resCpu] = await Promise.all([
          fetch("/api/system/gpu"),
          fetch("/api/system/cpu"),
        ]);
        const dataGpu: GpuResponse = await resGpu.json();
        const dataCpu: CpuData = await resCpu.json();
        if (!cancelled) {
          if (dataGpu.available && dataGpu.gpus.length > 0) {
            const g = dataGpu.gpus[0];
            setGpuData(g);
            setAvailable(true);
            utilHistory.current = [...utilHistory.current, g.util_pct].slice(-HISTORY_LEN);
            memHistory.current = [...memHistory.current, g.mem_pct].slice(-HISTORY_LEN);
          } else {
            setAvailable(false);
          }
          setCpuData(dataCpu);
          ramHistory.current = [...ramHistory.current, dataCpu.ram_pct].slice(-HISTORY_LEN);
          forceUpdate((n) => n + 1);
        }
      } catch {
        if (!cancelled) setAvailable(false);
      }
    };

    poll();
    const id = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (available === false) return null;
  if (!gpuData) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-600 animate-pulse">
        <span className="w-2 h-2 rounded-full bg-gray-600 inline-block" />
        GPU loading…
      </div>
    );
  }

  const tc = tempColor(gpuData.temp_c);
  const memUsedGb = (gpuData.mem_used_mb / 1024).toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const memTotalGb = (gpuData.mem_total_mb / 1024).toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  return (
    <div className="border-t border-gray-800 pt-6 mt-0">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">GPU</span>
        <span className="text-xs text-gray-500 truncate max-w-xs">{gpuData.name}</span>
        <span className="ml-auto text-gray-600 text-xs">Driver {gpuData.driver_version}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* GPU Utilisation */}
        <div className="bg-gray-800/50 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400 font-medium">GPU Load</span>
            <span className="text-sm font-bold text-green-400 tabular-nums">{gpuData.util_pct.toFixed(0)}%</span>
          </div>
          <Bar pct={gpuData.util_pct} color="#4ade80" />
          <div className="flex justify-end">
            <Sparkline data={utilHistory.current} color="#4ade80" />
          </div>
          <div className="flex gap-4 text-xs text-gray-500 pt-1 border-t border-gray-700">
            <span>🌡️ <span style={{ color: tc }}>{gpuData.temp_c.toFixed(0)}°C</span></span>
            <span>⚡ {gpuData.power_w.toFixed(0)} W</span>
          </div>
        </div>

        {/* VRAM */}
        <div className="bg-gray-800/50 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400 font-medium">VRAM</span>
            <span className="text-sm font-bold text-blue-400 tabular-nums">{gpuData.mem_pct.toLocaleString("da-DK")}%</span>
          </div>
          <Bar pct={gpuData.mem_pct} color="#60a5fa" />
          <div className="flex justify-end">
            <Sparkline data={memHistory.current} color="#60a5fa" />
          </div>
          <div className="text-xs text-gray-500 pt-1 border-t border-gray-700">
            {memUsedGb} GB / {memTotalGb} GB
          </div>
        </div>
      </div>

      {/* CPU + RAM row */}
      {cpuData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
          {/* Per-core CPU */}
          <div className="bg-gray-800/50 rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400 font-medium">CPU Kerner</span>
              <span className="text-sm font-bold text-orange-400 tabular-nums">{cpuData.cpu_pct.toFixed(0)}%</span>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 pt-1">
              {cpuData.cpu_cores.map((pct, i) => (
                <div key={i} className="space-y-0.5">
                  <div className="flex justify-between text-[10px] text-gray-500">
                    <span>Core {i}</span>
                    <span className="tabular-nums">{pct.toFixed(0)}%</span>
                  </div>
                  <Bar pct={pct} color="#fb923c" />
                </div>
              ))}
            </div>
          </div>

          {/* RAM */}
          <div className="bg-gray-800/50 rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400 font-medium">RAM</span>
              <span className="text-sm font-bold text-purple-400 tabular-nums">{cpuData.ram_pct.toFixed(0)}%</span>
            </div>
            <Bar pct={cpuData.ram_pct} color="#c084fc" />
            <div className="flex justify-end">
              <Sparkline data={ramHistory.current} color="#c084fc" />
            </div>
            <div className="text-xs text-gray-500 pt-1 border-t border-gray-700">
              {cpuData.ram_used_gb.toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} GB / {cpuData.ram_total_gb.toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} GB
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
